import { DEFAULT_SESSION_REPLAY_SAMPLE_RATE } from '@modernloop/shared/utils';

const requiredString = (key: string): string => {
  const value = import.meta.env[key];
  if (!value) {
    throw new Error(`Environment variable ${key} is not set`);
  }
  return value;
};

const optionalString = (key: string, defaultValue: string): string => {
  const value = import.meta.env[key];
  if (!value) {
    return defaultValue;
  }
  return value;
};

const maybeString = (key: string): string | undefined => {
  const value = import.meta.env[key];
  if (!value) {
    return undefined;
  }
  return value;
};

const requiredNumber = (key: string): number => {
  const value = import.meta.env[key];
  if (!value) {
    throw new Error(`Environment variable ${key} is not set`);
  }
  return parseInt(value, 10);
};

const optionalNumber = (key: string, defaultValue: number): number => {
  const value = import.meta.env[key];
  if (!value) {
    return defaultValue;
  }
  const parsed = parseInt(value, 10);
  return Number.isNaN(parsed) ? defaultValue : parsed;
};

const requiredOrigin = (key: string): string => {
  const url = new URL(requiredString(key));
  if (url.username || url.password || url.pathname !== '/' || url.search || url.hash) {
    throw new Error(`Environment variable ${key} must be an origin`);
  }
  return url.origin;
};

export const recruiterPortal = {
  url: requiredString('PUBLIC_MLOOP_HIRING_DASHBOARD_URL'),
};

export const auth0Config = {
  client_id: requiredString('PUBLIC_AUTH0_HIRING_DASHBOARD_CLIENT_ID'),
  domain: requiredString('PUBLIC_AUTH0_HIRING_DASHBOARD_DOMAIN'),
  audience: 'hasura', // This is just a name give to the application in Auth0, Nothing to do with Hasura
  useRefreshTokens: true,
};

export const amplitudeConfig = {
  apiKey: maybeString('PUBLIC_AMPLITUDE_API_KEY'),
};

export const netlifyConfig = {
  commitRef: optionalString('PUBLIC_COMMIT_REF', 'unknown'),
};

export const launchDarklyConfig = {
  clientSideID: requiredString('PUBLIC_LAUNCH_DARKLY_CLIENT_SIDE_ID'),
};

// FullCalendar Premium license key (`PUBLIC_FULLCALENDAR_LICENSE_KEY` in Doppler).
// Premium keys include a 1-year upgrade window. Before bumping `@fullcalendar/*`
// past the version covered by the current key, renew the license and update the
// secret in Doppler.
export const fullcalendarConfig = {
  licenseKey: maybeString('PUBLIC_FULLCALENDAR_LICENSE_KEY'),
};

export const backendConfig = {
  url: requiredString('PUBLIC_MLOOP_BACKEND_URL'), // no trailing slash
  shortUrlBase: requiredString('PUBLIC_MLOOP_SHORT_URL'),
};

export const platformApiConfig = {
  baseUrl: requiredOrigin('PUBLIC_MLOOP_PLATFORM_API_BASE_URL'),
};

export const logrocketConfig = {
  hiringDashboardClientSideID: requiredString('PUBLIC_LOG_ROCKET_HIRING_DASHBOARD_CLIENT_SIDE_ID'),
  candidatePortalClientSideID: requiredString('PUBLIC_LOG_ROCKET_CANDIDATE_PORTAL_CLIENT_SIDE_ID'),
  proxyUrl: requiredString('PUBLIC_LOG_ROCKET_PROXY_URL'),
};

export const enviromentConfig = {
  environment: requiredString('PUBLIC_MLOOP_ENVIRONMENT') as 'development' | 'staging' | 'production',
};

export const chromeExtConfig = {
  id: requiredString('PUBLIC_HIRING_DASHBOARD_CHROME_EXT_ID'),
  version: requiredString('PUBLIC_HIRING_DASHBOARD_CHROME_EXT_VERSION'),
};

export const CANDIDATE_PORTAL_BASE_URL = {
  url: requiredString('PUBLIC_MLOOP_CANDIDATE_PORTAL_URL'),
};

type DataDogEnv = 'dev' | 'stage' | 'prod';

export const DatadogConfig = {
  applicationId: requiredString('PUBLIC_DATADOG_HIRING_DASHBOARD_APPLICATION_ID'),
  clientToken: requiredString('PUBLIC_DATADOG_HIRING_DASHBOARD_CLIENT_TOKEN'),
  sessionSampleRate: requiredNumber('PUBLIC_DATADOG_HIRING_DASHBOARD_SESSION_SAMPLE_RATE'),
  // Session Replay defaults to off. The shared boundary also caps it at
  // MAX_SESSION_REPLAY_SAMPLE_RATE, so raising this variable alone cannot restore the
  // ~$970/month replay spend. See frontend/packages/mloop-shared/src/utils/datadog/index.ts.
  sessionReplaySampleRate: optionalNumber(
    'PUBLIC_DATADOG_HIRING_DASHBOARD_SESSION_REPLAY_SAMPLE_RATE',
    DEFAULT_SESSION_REPLAY_SAMPLE_RATE
  ),
  // Percent from 0 to 100. Override with the environment variable when required.
  traceSampleRate: optionalNumber('PUBLIC_DATADOG_HIRING_DASHBOARD_TRACE_SAMPLE_RATE', 20),
  service: requiredString('PUBLIC_DATADOG_HIRING_DASHBOARD_SERVICE'),
  env: requiredString('PUBLIC_DATADOG_ENV') as DataDogEnv,
  allowFallbackToLocalStorage: true,
  sessionPersistence: 'local-storage' as const, // required for chrome extensions
  storeContextsAcrossPages: true, // required for chrome extensions
};

export const GraphQLConfig = {
  persistedEnabled: maybeString('PUBLIC_GRAPHQL_PERSISTED_QUERY_ENABLED') === 'true',
  batchEnabled: maybeString('PUBLIC_GRAPHQL_BATCH_ENABLED') === 'true',
  batchInterval: requiredNumber('PUBLIC_GRAPHQL_BATCH_INTERVAL'),
  batchMax: requiredNumber('PUBLIC_GRAPHQL_BATCH_MAX'),
};
